import React, { useEffect, useRef, useState } from 'react';
import { InputBase } from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { AssistantCopy, MESSAGE_MAX_LENGTH } from './aiAssistant.constants';
import { ComposerArea, ComposerBox, ComposerFooter, QuotaFill, QuotaRow, QuotaTrack, SendButton } from './aiAssistant.styles';

/** Show the counter only when it matters. */
const COUNTER_THRESHOLD = MESSAGE_MAX_LENGTH - 500;

/** The meter stays hidden until this much (or less) of today's limit is left — a warning, not a permanent gauge. */
const QUOTA_WARNING_PERCENT = 30;
const QUOTA_WARNING_COLOR = '#FDB022';

/** What's left of today's AI limit — the tightest of the user's daily limits, so it hits 0% when sending would be refused. */
const QuotaMeter = ({ percent }) => (
    <QuotaRow role="status" aria-label={`${percent}% of today's AI limit left`}>
        <span>Today's AI limit</span>
        <QuotaTrack>
            <QuotaFill style={{ width: `${percent}%`, background: QUOTA_WARNING_COLOR }} />
        </QuotaTrack>
        <span>{percent > 0 ? `${percent}% left` : AssistantCopy.QUOTA_USED_UP}</span>
    </QuotaRow>
);

export const AssistantComposer = ({ isOpen, isBusy, onSend, quota }) => {
    const [value, setValue] = useState('');
    const inputRef = useRef(null);
    const canSend = !!value.trim() && !isBusy;

    useEffect(() => {
        // Wait for the slide-in so focusing doesn't jump the page.
        if (!isOpen) return undefined;
        const timer = setTimeout(() => inputRef.current?.focus(), 250);
        return () => clearTimeout(timer);
    }, [isOpen]);

    const submit = () => {
        if (!canSend) return;
        onSend(value);
        setValue('');
    };

    const onKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
        }
    };

    return (
        <ComposerArea>
            <ComposerBox>
                <InputBase
                    inputRef={inputRef}
                    multiline
                    maxRows={6}
                    value={value}
                    placeholder={AssistantCopy.PLACEHOLDER}
                    onChange={(event) => setValue(event.target.value)}
                    onKeyDown={onKeyDown}
                    inputProps={{ maxLength: MESSAGE_MAX_LENGTH, 'aria-label': 'Message the assistant' }}
                />
                <SendButton type="button" onClick={submit} disabled={!canSend} aria-label="Send">
                    <ArrowUpwardRoundedIcon />
                </SendButton>
            </ComposerBox>
            <ComposerFooter>
                <span>{AssistantCopy.DISCLAIMER}</span>
                {value.length > COUNTER_THRESHOLD && (
                    <span>
                        {value.length}/{MESSAGE_MAX_LENGTH}
                    </span>
                )}
            </ComposerFooter>
            {typeof quota === 'number' && quota <= QUOTA_WARNING_PERCENT && <QuotaMeter percent={Math.max(0, Math.min(100, quota))} />}
        </ComposerArea>
    );
};
